import React, { useState, useEffect, useRef, useCallback } from 'react';
import './custom-select.css';

const CaretDownIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * CustomSelect — кастомный выпадающий список,
 * заменяет нативный <select> для единообразного UI на мобильных.
 *
 * На десктопе: обычный дропдаун под триггером.
 * На мобильном: bottom sheet с затемнением.
 *
 * @param {string|number} value — текущее значение
 * @param {function} onChange — (value) => void
 * @param {Array<{value: string|number, label: string}>} options
 * @param {string} placeholder — текст когда ничего не выбрано
 * @param {boolean} searchable — показать поле поиска
 * @param {string} className — доп. класс на обёртку
 * @param {string} ariaLabel — aria-label для trigger
 */
const SWIPE_THRESHOLD = 80;

const CustomSelect = ({
  value,
  onChange,
  options = [],
  placeholder = '',
  searchable = false,
  className = '',
  ariaLabel = '',
  defaultOpen = false,
  onClose = null,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [search, setSearch] = useState('');
  const [swipeY, setSwipeY] = useState(0);
  const [isDismissing, setIsDismissing] = useState(false);
  const [fixedPos, setFixedPos] = useState(null);
  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const optionsRef = useRef(null);
  const dropdownRef = useRef(null);
  const touchRef = useRef({ startY: 0, currentY: 0, swiping: false });

  const selectedOption = options.find(
    (opt) => String(opt.value) === String(value)
  );
  const isPlaceholderState =
    value === '' || value === null || value === undefined || !selectedOption;
  const displayText = selectedOption ? selectedOption.label : placeholder;

  const filteredOptions =
    searchable && search
      ? options.filter((opt) =>
          opt.label.toLowerCase().includes(search.toLowerCase())
        )
      : options;

  const handleClose = useCallback(() => {
    setIsDismissing(true);
    setTimeout(() => {
      setIsOpen(false);
      setSearch('');
      setSwipeY(0);
      setIsDismissing(false);
      setFixedPos(null);
      if (onClose) onClose();
    }, 200);
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) return;
    if (searchable && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen || !optionsRef.current) return;
    const selectedEl = optionsRef.current.querySelector('.cs__option--selected');
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Swipe-to-dismiss touch handlers
  const handleTouchStart = useCallback((e) => {
    const optionsEl = optionsRef.current;
    if (optionsEl && optionsEl.scrollTop > 0) return;

    const touch = e.touches[0];
    touchRef.current = { startY: touch.clientY, currentY: touch.clientY, swiping: true };
    setSwipeY(0);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchRef.current.swiping) return;
    const touch = e.touches[0];
    const delta = touch.clientY - touchRef.current.startY;

    if (delta < 0) {
      touchRef.current.swiping = false;
      setSwipeY(0);
      return;
    }

    const optionsEl = optionsRef.current;
    if (optionsEl && optionsEl.scrollTop > 0) {
      touchRef.current.swiping = false;
      setSwipeY(0);
      return;
    }

    if (delta > 10) {
      e.preventDefault();
    }

    touchRef.current.currentY = touch.clientY;
    setSwipeY(delta);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchRef.current.swiping) {
      setSwipeY(0);
      return;
    }
    const delta = touchRef.current.currentY - touchRef.current.startY;
    touchRef.current.swiping = false;

    if (delta >= SWIPE_THRESHOLD) {
      handleClose();
    } else {
      setSwipeY(0);
    }
  }, [handleClose]);

  const handleSelect = (optValue) => {
    onChange(optValue);
    setIsDismissing(true);
    setTimeout(() => {
      setIsOpen(false);
      setSearch('');
      setSwipeY(0);
      setIsDismissing(false);
      if (onClose) onClose();
    }, 150);
  };

  const handleToggle = () => {
    if (isOpen) {
      handleClose();
    } else {
      if (wrapRef.current && window.innerWidth > 768) {
        const rect = wrapRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const needsFixed = spaceBelow < 200;
        if (needsFixed) {
          const spaceAbove = rect.top;
          if (spaceAbove > spaceBelow) {
            setFixedPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width });
          } else {
            setFixedPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
          }
        }
      }
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  const fixedStyle = fixedPos ? {
    position: 'fixed',
    top: fixedPos.top != null ? fixedPos.top : 'auto',
    bottom: fixedPos.bottom != null ? fixedPos.bottom : 'auto',
    left: fixedPos.left,
    width: fixedPos.width,
    right: 'auto',
  } : undefined;

  const dropdownStyle = swipeY > 0
    ? { ...fixedStyle, transform: `translateY(${swipeY}px)`, transition: 'none' }
    : isDismissing
      ? { ...fixedStyle, transform: 'translateY(100%)', transition: 'transform 0.2s ease' }
      : fixedStyle;

  const backdropStyle = isDismissing
    ? { opacity: 0, transition: 'opacity 0.2s ease' }
    : swipeY > 0
      ? { opacity: Math.max(0, 1 - swipeY / 300) }
      : undefined;

  return (
    <div className={`cs ${className}`} ref={wrapRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        className={`cs__trigger ${isOpen ? 'cs__trigger--open' : ''}`}
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel || placeholder || 'Выберите значение'}
        tabIndex={0}
      >
        <span
          className={`cs__value ${isPlaceholderState ? 'cs__value--placeholder' : ''}`}
        >
          {displayText}
        </span>
        <span className="cs__icon">
          <CaretDownIcon />
        </span>
      </button>

      {isOpen && (
        <>
          <div
            className="cs__backdrop"
            onClick={handleClose}
            onTouchEnd={handleClose}
            aria-hidden="true"
            style={backdropStyle}
          />
          <div
            ref={dropdownRef}
            className="cs__dropdown"
            role="listbox"
            style={dropdownStyle}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {searchable && (
              <div className="cs__search-wrap">
                <input
                  ref={searchRef}
                  type="text"
                  className="cs__search"
                  placeholder="Поиск..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Поиск в списке"
                />
              </div>
            )}
            <div className="cs__options" ref={optionsRef}>
              {filteredOptions.map((opt) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  role="option"
                  aria-selected={String(opt.value) === String(value)}
                  className={`cs__option ${
                    String(opt.value) === String(value)
                      ? 'cs__option--selected'
                      : ''
                  }`}
                  onClick={() => handleSelect(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
              {filteredOptions.length === 0 && (
                <div className="cs__empty">Ничего не найдено</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomSelect;
